<!--
	SPDX-FileCopyrightText: Ferdinand Thiessen <opensource@fthiessen.de>
	SPDX-License-Identifier: AGPL-3.0-or-later
-->

<template>
	<div :class="$style.wrapper">
		<h3 :class="$style.headline">
			{{ categoryName }}
		</h3>
		<CheckList
			:class="$style.list"
			:setup-checks="truncatedChecks"
			@show-details="onForwardShowDetails" />
	</div>
</template>

<script setup lang="ts">
import type { ISetupCheck } from '../types'

import { translatePlural as n } from '@nextcloud/l10n'
import { useWindowSize } from '@vueuse/core'
import { computed } from 'vue'

import { MAX_ENTRIES_IN_LIST, MAX_ENTRIES_IN_LIST_MOBILE } from '../constants.ts'
import CheckList from './CheckList.vue'

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const props = defineProps<{
	category: string
	categoryName: string
	setupChecks: ISetupCheck[]
}>()

const emit = defineEmits<{
	(e: 'show-details', category: string, v?: ISetupCheck): void
}>()

const { width: windowWidth } = useWindowSize()
const maxEntriesInList = computed(() =>
	windowWidth.value < 768 ? MAX_ENTRIES_IN_LIST_MOBILE : MAX_ENTRIES_IN_LIST,
)

/**
 * Setup checks sorted and truncated
 */
const truncatedChecks = computed(() => {
	if (props.setupChecks.length > maxEntriesInList.value) {
		const name = n(
			'security_guard',
			'One more entry',
			'%n more entries',
			props.setupChecks.length - maxEntriesInList.value + 1,
		)
		return [
			...props.setupChecks.slice(0, maxEntriesInList.value - 1),
			{
				severity: props.setupChecks[maxEntriesInList.value].severity,
				name,
				dummy: true,
			} as ISetupCheck | (ISetupCheck & { dummy: true }),
		]
	} else {
		return props.setupChecks
	}
})

/**
 * Forward the show details event, also handle the "truncated" dummy messages
 * @param v The setup check for which to show details
 */
const onForwardShowDetails = (v: ISetupCheck & { dummy?: boolean }) => {
	if (v?.dummy) {
		emit('show-details', props.category)
	} else {
		emit('show-details', props.category, v)
	}
}
</script>

<style module lang="scss">
// The list content
.list {
	// align with headline
	margin-inline-start: -10px;
}

// Outer wrapper around a category
.wrapper {
	display: flex;
	flex-direction: column;
	gap: 10px;

	// make all entries same width
	width: 0;
	flex: 1 1 0;
}

// The list headline / name
.headline {
	font-size: 18px;
	font-weight: bold;
	line-height: 1.5;
}

@media only screen and (max-width: 768px) {
	.wrapper {
		width: unset;
		gap: unset;
	}
}
</style>
